import {
  ChatBankAccount,
  ChatBubbleExtras,
  ChatCompanyDebt,
  ChatDebtStatus,
  ChatMessageResponse,
  ChatServiceProduct,
} from './types'
import { formatChatMoney } from './chatFormatters'
import { normalizeCurrency } from './chatMappers'

/**
 * Backend'in yapılandırılmış payload göndermediği bilinen düz-metin yanıtlarını frontend'de
 * ayrıştırıp kart bileşenlerine çevirir. Backend ilgili payload'ı (DebtStatus vb.) göndermeye
 * başladığında bu ayrıştırıcılar devre dışı kalır (payload her zaman önceliklidir) ve
 * silinebilirler. Kalıp tanınmazsa metin olduğu gibi bırakılır — veri kaybı riski yok.
 */

/** Türkçe biçimli sayı ("114.370,20" / "-134,50" / sonda cümle noktası olabilir) → number. */
function parseTrNumber(raw: string): number | null {
  const cleaned = raw.trim().replace(/[.,]+$/, '')
  if (!cleaned) return null
  const normalized = cleaned.replace(/\./g, '').replace(',', '.')
  const value = Number(normalized)
  return Number.isFinite(value) ? value : null
}

const OVERDUE_HEADER = /vadesi geçmiş\/ödenmesi gereken toplam\s*:?\s*([-\d.,]+)/i
const OVERDUE_LINE = /^(.+?)\s*:\s*toplam\s+([-\d.,]+)\s*,\s*vadesi geçen\s+([-\d.,]+)(?:\s*\((\d+)\s*kalem\))?/i

/**
 * "Açık Faturalarım" düz-metin yanıtı:
 *   Vadesi geçmiş/ödenmesi gereken toplam: 114.161,31.
 *   Detay:
 *   • Web Center: toplam 114.370,20, vadesi geçen 114.370,20 (756 kalem)
 * → grup satırları + vadesi geçen toplam footer'ı olan borç kartına dönüştürülür.
 * Başlık toplamı veya satırlardan biri ayrıştırılamazsa null döner (metin bozulmadan kalır).
 */
export function parseOverdueInvoicesText(text: string): { text: string; debtStatus: ChatDebtStatus } | null {
  const headerMatch = text.match(OVERDUE_HEADER)
  if (!headerMatch) return null
  const total = parseTrNumber(headerMatch[1])
  if (total === null) return null

  const companies: ChatCompanyDebt[] = []
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim().replace(/^[•\-*]\s*/, '')
    if (!line || /vadesi geçmiş\/ödenmesi gereken/i.test(line) || /^detay\s*:?$/i.test(line)) continue

    const match = line.match(OVERDUE_LINE)
    if (!match) return null // tanınmayan satır → hiç dokunma, ham metin gösterilsin

    const groupTotal = parseTrNumber(match[2])
    const overdue = parseTrNumber(match[3])
    if (groupTotal === null || overdue === null) return null

    const detailParts = [`Toplam ${formatChatMoney(groupTotal)}`]
    if (match[4]) detailParts.push(`${match[4]} kalem`)

    companies.push({
      companyCode: match[1].trim(),
      companyName: match[1].trim(),
      amounts: [{ currency: 'TRY', amount: overdue }],
      detail: detailParts.join(' · '),
    })
  }
  if (companies.length === 0) return null

  return {
    text: 'Vadesi geçmiş / ödenmesi gereken faturalarınızın özeti:',
    debtStatus: { companies, total: { currency: 'TRY', amount: total }, totalLabel: 'Vadesi Geçen Toplam' },
  }
}

const BANK_ACCOUNT_HEADER = /^banka hesap/i
/** "Index · AKBANK · USD: TR76…" — döviz ve IBAN serbest yakalanır, şekil kontrolü ayrı yapılır. */
const BANK_ACCOUNT_LINE = /^(.+?)\s*·\s*(.+?)\s*·\s*([^·:]+?)\s*:\s*(.+)$/
/** Doğrulama DEĞİL: yalnız "IBAN mı yakaladım" kontrolü. Gerçek veride harf içeren IBAN var. */
const IBAN_SHAPE = /^TR[0-9A-Z]{20,30}$/
/** Ayrıştırıcının tanıdığı döviz kodları. Bunun dışındaki bir yakalama uydurulmaz — satır düşer. */
const KNOWN_CURRENCY_TOKENS = new Set(['TL', 'TRY', 'USD', 'EUR'])

/**
 * "Banka Hesapları (IBAN)" düz-metin yanıtı:
 *   Banka hesapları:
 *   • Index · AKBANK · USD: TR760004600763001000052472
 * → şirket/banka/döviz kırılımlı hesap satırlarına dönüştürülür.
 * Tanınmayan satır varsa null döner (metin bozulmadan kalır) — veri kaybı riski yok.
 */
export function parseBankAccountsText(text: string): { text: string; bankAccounts: ChatBankAccount[] } | null {
  const bankAccounts: ChatBankAccount[] = []

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim().replace(/^[•\-*]\s*/, '')
    if (!line || BANK_ACCOUNT_HEADER.test(line)) continue

    const match = line.match(BANK_ACCOUNT_LINE)
    if (!match) return null // tanınmayan satır → hiç dokunma, ham metin gösterilsin

    const currencyToken = match[3].trim().toUpperCase()
    if (!KNOWN_CURRENCY_TOKENS.has(currencyToken)) return null // bilinmeyen döviz uydurulmaz, ham metin gösterilsin

    const iban = match[4].replace(/\s/g, '').toUpperCase()
    if (!IBAN_SHAPE.test(iban)) return null

    bankAccounts.push({
      companyName: match[1].trim(),
      bankName: match[2].trim(),
      currency: normalizeCurrency(currencyToken),
      iban,
    })
  }
  if (bankAccounts.length === 0) return null

  return { text: 'Banka hesaplarımız:', bankAccounts }
}

const SERVICE_HEADER = /^servisteki ürünler/i
/**
 * Kimlik parçaları tek jetonludur (örn. "seri 300532797" / "form 000300532797"). Kalıp kasıtlı
 * dar tutulur: "Seri numarası bekleniyor" veya "Form Kapatıldı" gibi bir durum ifadesi bu
 * kalıplara UYMAMALI ki serbest durum dalına düşsün — geniş bir `.+` bunları yutup durumu
 * sessizce kaybeder ya da olmayan bir kimlik uydurur.
 */
const SERVICE_SERIAL = /^seri\s+(\S+)$/i
const SERVICE_FORM = /^form\s+(\d[\d\s-]*)$/i
/** "İade Faturası Bekleniyor B.F.: 3419,12 TRY Kur: 1,00000" → durum / tutar / döviz / kur. */
const SERVICE_PRICE = /^(.*?)\s*B\.?\s?F\.?\s*:\s*([-\d.,]+)\s*([A-Za-z]{3})?\s*(?:Kur\s*:\s*([-\d.,]+))?\s*$/i
/**
 * SERVICE_PRICE'ın tanıdığı "B.F.:" / "Kur:" imlasının gevşek hâli. Katı kalıba (SERVICE_PRICE) uymayan
 * ama bu imzayı taşıyan parça (dipnot, beklenmeyen noktalama, kur sonrası ekstra metin vb.) tutar/döviz
 * verisi taşıyormuş gibi görünür; serbest durum metnine indirgenirse o veri sessizce kaybolur.
 */
const SERVICE_PRICE_MARKER = /B\.?\s?F\.?\s*:|Kur\s*:/i

/**
 * "Servisteki ürünlerim" (getArizaTakip) düz-metin yanıtı:
 *   Servisteki ürünleriniz (2):
 *   • HP CF543X … · seri 300532797 · İade Faturası Bekleniyor B.F.: 3419,12 TRY Kur: 1,00000 · form 000300532797
 * → servis kayıtlarına dönüştürülür. Kayıt sayısı BAŞLIKTAN okunmaz; kart kendi sayar.
 * Form no taşımayan satır bu kalıba ait değildir → null döner ve ham metin bozulmadan kalır.
 */
export function parseServiceProductsText(text: string): { text: string; serviceProducts: ChatServiceProduct[] } | null {
  const serviceProducts: ChatServiceProduct[] = []

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim().replace(/^[•\-*]\s*/, '')
    if (!line || SERVICE_HEADER.test(line)) continue

    const parts = line
      .split('·')
      .map(part => part.trim())
      .filter(Boolean)
    const productName = parts.shift()
    if (!productName) return null

    const item: ChatServiceProduct = { productName }

    for (const part of parts) {
      const serial = part.match(SERVICE_SERIAL)
      if (serial) {
        // Aynı türden ikinci parça (ör. "seri 1 · seri 2") hangisinin doğru olduğunu bilemeyiz.
        if (item.serialNumber) return null
        item.serialNumber = serial[1].trim()
        continue
      }

      const form = part.match(SERVICE_FORM)
      if (form) {
        if (item.formNumber) return null // tekrar eden form no → uydurma yapılmaz
        item.formNumber = form[1].trim()
        continue
      }

      const price = part.match(SERVICE_PRICE)
      if (price) {
        if (item.unitPrice !== undefined) return null // tekrar eden tutar parçası → uydurma yapılmaz
        const unitPrice = parseTrNumber(price[2])
        if (unitPrice === null) return null // tutar okunamadıysa uydurma yapılmaz
        const status = price[1].trim()
        // Satırda daha önce serbest bir durum geldiyse ve bu tutar parçası da kendi durum metnini
        // taşıyorsa, iki çelişen durum sinyali var demektir; hangisi doğru bilinemez, uydurma yapılmaz.
        if (status && item.status) return null
        if (status) item.status = status
        item.unitPrice = unitPrice
        item.currency = normalizeCurrency(price[3])
        item.exchangeRate = price[4] ? parseTrNumber(price[4]) : null
        continue
      }

      // "B.F.:" / "Kur:" imzası taşıyan ama katı kalıba uymayan parça tutar/döviz verisi taşıyormuş
      // gibi görünür; durum metnine indirgenirse o veri sessizce kaybolur → null dönülür.
      if (SERVICE_PRICE_MARKER.test(part)) return null

      // B.F. taşımayan serbest parça durumdur ("İşlemde"). İkinci kez gelirse kalıp tanınmıyor.
      if (item.status) return null
      item.status = part
    }

    // Form no servis kaydının değişmez alanı; yoksa bu satır bu kalıba ait değil.
    if (!item.formNumber) return null

    serviceProducts.push(item)
  }

  if (serviceProducts.length === 0) return null

  return { text: '', serviceProducts }
}

/** Yanıttaki payload'ları balon ekstralarına çevirir; payload yoksa bilinen metin kalıplarını dener. */
export function structureChatMessage(response: ChatMessageResponse): { text: string; extras: ChatBubbleExtras } {
  const extras: ChatBubbleExtras = {
    form: response.form ?? undefined,
    basketProposal: response.basketProposal ?? undefined,
    orders: response.orders?.length ? response.orders : undefined,
    balances: response.balances?.length ? response.balances : undefined,
    debtStatus: response.debtStatus ?? undefined,
    posLimits: response.posLimits ?? undefined,
    invoices: response.invoices?.length ? response.invoices : undefined,
    products: response.products?.length ? response.products : undefined,
    // Yalnız kartlarla birlikte anlamlı; kart yoksa "Tümünü Getir" tek başına gösterilmez.
    productSearch: response.products?.length ? response.productSearch ?? undefined : undefined,
    brands: response.brands?.length ? response.brands : undefined,
    categories: response.categories?.length ? response.categories : undefined,
    representatives: response.representatives?.length ? response.representatives : undefined,
    navigationActions: response.navigationActions?.length ? response.navigationActions : undefined,
    externalLinks: response.externalLinks?.length ? response.externalLinks : undefined,
    promoVideo: response.promoVideo ?? undefined,
    table: response.table ?? undefined,
    guaranteeDetail: response.guaranteeDetail ?? undefined,
    cargoTracking: response.cargoTracking ?? undefined,
    serialNumberChecks: response.serialNumberChecks?.length ? response.serialNumberChecks : undefined,
    bankAccounts: response.bankAccounts?.length ? response.bankAccounts : undefined,
    coupons: response.coupons?.length ? response.coupons : undefined,
    creditCards: response.creditCards?.length ? response.creditCards : undefined,
    addresses: response.addresses?.length ? response.addresses : undefined,
    commissionRates: response.commissionRates ?? undefined,
    // Sepet komisyon kartı: metni YUTMAZ (cardReplacesText'e girmez). Metin hem soruya cevabı
    // hem de karta girmeyen kalın kampanya notunu taşır; kart ise şirket sekmelerini verir.
    basketCommissionRates: response.basketCommissionRates ?? undefined,
    serviceProducts: response.serviceProducts?.length ? response.serviceProducts : undefined,
  }

  const hasStructured = Object.values(extras).some(Boolean)
  if (!hasStructured) {
    const overdue = parseOverdueInvoicesText(response.text)
    if (overdue) return { text: overdue.text, extras: { ...extras, debtStatus: overdue.debtStatus } }

    const accounts = parseBankAccountsText(response.text)
    if (accounts) return { text: accounts.text, extras: { ...extras, bankAccounts: accounts.bankAccounts } }

    const services = parseServiceProductsText(response.text)
    if (services) return { text: services.text, extras: { ...extras, serviceProducts: services.serviceProducts } }
  }

  // Kart kaydın tamamını gösteriyor → backend metni aynı veriyi tekrarlar, gizlenir (modal da
  // kart varken `text`'i kullanmaz). Seri no listesi hariç: çok kayıtlı yanıtta metin kapsam
  // bilgisi taşır ve kartların üstünde giriş satırı olarak iş görür.
  // Komisyon kartı da kendi başlığını taşır ("Kredi Kartı Komisyon Oranları"); metin giriş satırı
  // olarak bırakılırsa başlığı birebir tekrarlar. Ek bağlam için kartın `note` alanı var.
  // Servis kartı da kendi başlığını ve kayıt sayısını taşır ("Servisteki Ürünleriniz (2)"); metin
  // bırakılırsa hem başlık hem tüm alanlar iki kez basılır.
  const cardReplacesText = Boolean(
    extras.guaranteeDetail || extras.cargoTracking || extras.commissionRates || extras.serviceProducts?.length
  )

  return { text: cardReplacesText ? '' : response.text, extras }
}
